type SentryFrameLike = {
  filename?: string;
};

type SentryExceptionValueLike = {
  value?: string;
  stacktrace?: {
    frames?: SentryFrameLike[];
  };
};

type SentryEventLike = {
  message?: string;
  fingerprint?: string[];
  location?: string;
  extra?: Record<string, unknown>;
  request?: {
    url?: string;
  };
  tags?: Record<string, unknown>;
  exception?: {
    values?: SentryExceptionValueLike[];
  };
};

type SentryHintLike = {
  originalException?: unknown;
  syntheticException?: unknown;
};

type ShouldDropSentryEventArgs = {
  event: SentryEventLike;
  hint: SentryHintLike | undefined;
};

type TrackingEnvironmentLike = {
  isExtension: boolean;
  isLocalhost: boolean;
  isStorybook: boolean;
};

export const isGoogleTranslateActive = () => {
  if (typeof document === 'undefined') return false;

  try {
    const selectors = [
      'iframe.goog-te-banner-frame',
      '#goog-gt-tt',
      '.goog-te-spinner-pos',
      '.goog-te-combo',
      '.goog-te-banner-frame',
      '.goog-te-gadget',
    ];

    if (selectors.some((selector) => document.querySelector(selector))) {
      return true;
    }

    const htmlClass = document.documentElement?.className ?? '';
    if (
      htmlClass.includes('translated-ltr') ||
      htmlClass.includes('translated-rtl')
    ) {
      return true;
    }

    return !!document.querySelector('script[src*="translate.google.com"]');
  } catch {
    return false;
  }
};

const domMutationMessage =
  /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/;
const maximumUpdateDepthMessage = /Maximum update depth exceeded/i;
const rawApiObjectUnhandledRejectionMessage =
  /Object captured as promise rejection with keys:\s*code,\s*issues,\s*message/i;
const birdieEmbedPath = /\/widget\/embed\//i;
const birdieBundleName = /main\.[^.]+\.js(?::\d+(?::\d+)?)?$/i;
const birdieBundleReference =
  /\/widget\/embed\/main\.[^\s)]+\.js(?::\d+(?::\d+)?)?/i;
const uuidLikeSegment =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const longHexLikeSegment = /^[0-9a-f]{16,}$/i;

type SerializedApiErrorLike = {
  code?: string;
  message?: string;
  issues?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const getEventMessage = (event: SentryEventLike): string => {
  return event.message || event.exception?.values?.[0]?.value || '';
};

const getHintMessage = (hint: SentryHintLike | undefined): string => {
  const original = hint?.originalException;

  if (typeof original === 'string') return original;

  if (
    original &&
    typeof (original as { message?: unknown }).message === 'string'
  ) {
    return (original as { message: string }).message;
  }

  return '';
};

const isBirdieEmbedFrame = (frame: SentryFrameLike): boolean => {
  const filename = frame.filename;
  if (!filename) return false;

  return birdieEmbedPath.test(filename) && birdieBundleName.test(filename);
};

const isBirdieEmbedLocation = (value: string): boolean => {
  return birdieEmbedPath.test(value) && birdieBundleName.test(value);
};

const hasBirdieEmbedReference = (value: string): boolean => {
  return birdieBundleReference.test(value.replaceAll('\\', '/'));
};

const getStackLikeString = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'string') return value;

  if (typeof value !== 'object') return '';

  if (typeof (value as { stack?: unknown }).stack === 'string') {
    return (value as { stack: string }).stack;
  }

  if ('reason' in value) {
    return getStackLikeString((value as { reason?: unknown }).reason);
  }

  if ('error' in value) {
    return getStackLikeString((value as { error?: unknown }).error);
  }

  return '';
};

const unwrapUnhandledRejectionValue = (value: unknown): unknown => {
  if (!isRecord(value)) return value;

  if ('reason' in value) {
    return unwrapUnhandledRejectionValue(value.reason);
  }

  if ('error' in value) {
    return unwrapUnhandledRejectionValue(value.error);
  }

  return value;
};

const isSerializedApiError = (
  value: unknown
): value is SerializedApiErrorLike => {
  if (!isRecord(value)) return false;

  const message = value.message;
  if (typeof message !== 'string' || message.trim().length === 0) return false;

  return 'code' in value || 'issues' in value;
};

const getUnhandledApiRejectionPayload = (
  event: SentryEventLike,
  hint: SentryHintLike | undefined
): SerializedApiErrorLike | undefined => {
  const candidates = [
    event.extra?.__serialized__,
    hint?.originalException,
    hint?.syntheticException,
  ];

  for (const candidate of candidates) {
    const unwrapped = unwrapUnhandledRejectionValue(candidate);
    if (isSerializedApiError(unwrapped)) {
      return unwrapped;
    }
  }

  return undefined;
};

const getEventUrl = (event: SentryEventLike): string | undefined => {
  const taggedUrl = event.tags?.url;
  return (
    event.request?.url ||
    (typeof taggedUrl === 'string' ? taggedUrl : undefined)
  );
};

const normalizeApiRejectionRoute = (url: string | undefined): string => {
  if (!url) return 'unknown-route';

  try {
    const pathname = new URL(url).pathname || '/';
    const segments = pathname
      .split('/')
      .filter(Boolean)
      .map((segment) =>
        uuidLikeSegment.test(segment) || longHexLikeSegment.test(segment)
          ? ':id'
          : segment
      );

    return segments.length > 0 ? `/${segments.join('/')}` : '/';
  } catch {
    if (url.startsWith('/')) return url;
    return 'unknown-route';
  }
};

const isBirdieEvent = (
  event: SentryEventLike,
  hint: SentryHintLike | undefined
): boolean => {
  const frames = event.exception?.values?.flatMap(
    (value) => value.stacktrace?.frames ?? []
  );

  if (frames?.some(isBirdieEmbedFrame)) {
    return true;
  }

  if (event.location && isBirdieEmbedLocation(event.location)) {
    return true;
  }

  return [hint?.originalException, hint?.syntheticException].some((value) => {
    const stack = getStackLikeString(value);
    return stack ? hasBirdieEmbedReference(stack) : false;
  });
};

const normalizeFrameFilename = (filename: string): string => {
  const normalized = filename.replaceAll('\\', '/');
  const withoutHash = normalized.split('#')[0] ?? normalized;
  const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
  const withoutLine = withoutQuery.replace(/:\d+:\d+$/, '');

  const appSrcMatch = withoutLine.match(/(?:^|\/)(app\/src\/.+)$/);
  if (appSrcMatch?.[1]) return appSrcMatch[1];

  const srcMatch = withoutLine.match(/(?:^|\/)(src\/.+)$/);
  if (srcMatch?.[1]) return srcMatch[1];

  const libsSrcMatch = withoutLine.match(/(?:^|\/)(libs\/[^/]+\/src\/.+)$/);
  if (libsSrcMatch?.[1]) return libsSrcMatch[1];

  return withoutLine.replace(/^(\.\.\/)+/, '');
};

const isFirstPartyFrame = (frame: SentryFrameLike): boolean => {
  const filename = frame.filename;
  if (!filename) return false;
  if (filename.includes('node_modules')) return false;

  const normalized = normalizeFrameFilename(filename);

  return (
    normalized.startsWith('app/src/') ||
    normalized.startsWith('src/') ||
    normalized.startsWith('libs/')
  );
};

const getMaximumUpdateDepthComponentKey = (event: SentryEventLike): string => {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? [];

  const firstPartyFrames = frames.filter(isFirstPartyFrame);

  const appFrame = firstPartyFrames.find((frame) => {
    const filename = frame.filename;
    if (!filename) return false;

    const normalized = normalizeFrameFilename(filename);
    return normalized.startsWith('app/src/') || normalized.startsWith('src/');
  });

  if (appFrame?.filename) {
    return normalizeFrameFilename(appFrame.filename);
  }

  const fallbackFrame = firstPartyFrames[0];
  if (fallbackFrame?.filename) {
    return normalizeFrameFilename(fallbackFrame.filename);
  }

  return 'unknown-component';
};

export const withMaximumUpdateDepthFingerprint = <T extends SentryEventLike>(
  event: T
): T => {
  if (!maximumUpdateDepthMessage.test(getEventMessage(event))) {
    return event;
  }

  event.fingerprint = [
    '{{ default }}',
    'maximum-update-depth-exceeded',
    getMaximumUpdateDepthComponentKey(event),
  ];

  return event;
};

export const withUnhandledApiRejectionFingerprint = <T extends SentryEventLike>(
  event: T,
  hint: SentryHintLike | undefined
): T => {
  const eventMessage = getEventMessage(event);
  if (!rawApiObjectUnhandledRejectionMessage.test(eventMessage)) {
    return event;
  }

  const payload = getUnhandledApiRejectionPayload(event, hint);
  if (!payload?.message) return event;

  const code =
    typeof payload.code === 'string' && payload.code.trim().length > 0
      ? payload.code
      : 'unknown-code';
  const route = normalizeApiRejectionRoute(getEventUrl(event));
  const title = `Unhandled API rejection (${code}): ${payload.message}`;

  event.message = title;
  if (event.exception?.values?.[0]) {
    event.exception.values[0].value = title;
  }

  event.fingerprint = [
    '{{ default }}',
    'unhandled-api-rejection',
    code,
    payload.message,
    route,
  ];
  event.tags = {
    ...event.tags,
    error_kind: 'api-object-rejection',
    error_source: 'global_unhandledrejection',
    api_error_code: code,
    api_error_route: route,
  };
  event.extra = {
    ...event.extra,
    apiRejection: {
      code,
      message: payload.message,
      route,
      source: 'global_unhandledrejection',
    },
  };

  return event;
};

export const isTrackingEnabled = ({
  isExtension,
  isLocalhost,
  isStorybook,
}: TrackingEnvironmentLike): boolean => {
  return !isExtension && !isLocalhost && !isStorybook;
};

export const shouldDropSentryEvent = ({
  event,
  hint,
}: ShouldDropSentryEventArgs): boolean => {
  if (isBirdieEvent(event, hint)) {
    return true;
  }

  if (isGoogleTranslateActive()) {
    return true;
  }

  const eventMessage = getEventMessage(event);
  const hintMessage = getHintMessage(hint);

  return (
    domMutationMessage.test(eventMessage) ||
    domMutationMessage.test(hintMessage)
  );
};
